import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { mediaUrl } from '../utils/blogUtils';
import { formatGoalAssist } from '../utils/tournamentGoalUtils';
import './ClubTournament.css';

const parseLocalDateTime = (dateStr) => {
  if (!dateStr) return null;
  const raw = String(dateStr);
  if (!raw.includes('Z') && !/[+-]\d{2}:\d{2}$/.test(raw)) {
    const [datePart, timePart = '00:00:00'] = raw.split('T');
    if (datePart) {
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes, seconds] = timePart.split(':').map(Number);
      return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0, 0);
    }
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = parseLocalDateTime(dateStr);
  if (!d) return '';
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const sortByMatchDate = (fixtures, direction = 'asc') => {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...fixtures].sort((left, right) => {
    const leftTime = left.matchDate ? new Date(left.matchDate).getTime() : Number.MAX_SAFE_INTEGER;
    const rightTime = right.matchDate ? new Date(right.matchDate).getTime() : Number.MAX_SAFE_INTEGER;
    return (leftTime - rightTime) * multiplier;
  });
};

const FixtureCard = ({ fixture }) => (
  <div
    className={`tournament-fixture-card tournament-fixture-card--public${fixture.status === 'LIVE' ? ' tournament-fixture-card--live' : ''}${fixture.status === 'SCHEDULED' ? ' tournament-fixture-card--scheduled' : ''}${fixture.status === 'FINAL' ? ' tournament-fixture-card--final-card' : ''}`}
  >
    <div className="tournament-fixture-card__meta">
      {fixture.round && <span className="tournament-fixture-card__round">{fixture.round}</span>}
      {fixture.status === 'LIVE' && <span className="tournament-live-dot">LIVE</span>}
      {fixture.status === 'FINAL' && <span className="tournament-fixture-card__final">FT</span>}
      {fixture.status === 'SCHEDULED' && <span className="tournament-fixture-card__scheduled">Upcoming</span>}
      {fixture.matchDate && <span>{formatDateTime(fixture.matchDate)}</span>}
      {fixture.location && <span>{fixture.location}</span>}
    </div>

    <div className="tournament-scoreboard tournament-scoreboard--public">
      <div className="tournament-scoreboard__team">
        <span className="tournament-scoreboard__name">{fixture.homeTeam}</span>
        {fixture.status !== 'SCHEDULED' && (
          <span className="tournament-scoreboard__score">{fixture.scoreHome ?? 0}</span>
        )}
      </div>
      <span className="tournament-scoreboard__divider">{fixture.status === 'SCHEDULED' ? 'vs' : '–'}</span>
      <div className="tournament-scoreboard__team">
        <span className="tournament-scoreboard__name">{fixture.awayTeam}</span>
        {fixture.status !== 'SCHEDULED' && (
          <span className="tournament-scoreboard__score">{fixture.scoreAway ?? 0}</span>
        )}
      </div>
    </div>

    {(fixture.goals || []).length > 0 && (
      <ul className="tournament-goals-list tournament-goals-list--public">
        {[...(fixture.goals || [])]
          .sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0))
          .map((g) => {
            const assistLabel = formatGoalAssist(g);
            return (
            <li key={g.id}>
              <span>{g.minute}&apos;</span>
              <strong>{g.scorerPlayerName}</strong>
              {assistLabel && <span> ({assistLabel})</span>}
            </li>
            );
          })}
      </ul>
    )}
  </div>
);

const PublicClubTournament = () => {
  const { token } = useParams();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadTournament = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPublicTournament(token);
      setTournament(data);
      setError('');
    } catch (err) {
      setError(
        api.isTournamentApiUnavailable(err)
          ? 'Tournament page is not available yet. The backend may still be deploying — please try again in a few minutes.'
          : (err.message || 'Tournament not found')
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadTournament();
  }, [loadTournament]);

  const liveFixtures = useMemo(
    () => sortByMatchDate((tournament?.fixtures || []).filter((f) => f.status === 'LIVE')),
    [tournament]
  );

  const upcomingFixtures = useMemo(
    () => sortByMatchDate((tournament?.fixtures || []).filter((f) => f.status === 'SCHEDULED')),
    [tournament]
  );

  const completedFixtures = useMemo(
    () => sortByMatchDate((tournament?.fixtures || []).filter((f) => f.status === 'FINAL'), 'desc'),
    [tournament]
  );

  useEffect(() => {
    const interval = liveFixtures.length > 0
      ? setInterval(loadTournament, 30000)
      : null;
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [liveFixtures.length, loadTournament]);

  const topScorers = useMemo(() => {
    return [...(tournament?.playerStats || [])]
      .filter((p) => p.goals > 0 || p.assists > 0)
      .sort((a, b) => b.goals - a.goals || b.assists - a.assists)
      .slice(0, 10);
  }, [tournament]);

  const handleShare = () => {
    if (!tournament) return;
    const url = api.shareTournamentViaWhatsApp({
      tournamentName: tournament.name,
      clubName: tournament.clubName,
      shareToken: token,
      status: tournament.status,
      fixtures: tournament.fixtures,
    });
    window.open(url, '_blank');
  };

  if (loading) return <div className="loading">Loading tournament…</div>;
  if (error) return <div className="alert alert-error public-tournament__error">{error}</div>;
  if (!tournament) return null;

  const hasFixtures = (tournament.fixtures || []).length > 0;

  return (
    <div className="public-tournament">
      <header className="public-tournament__header">
        {tournament.clubLogoUrl && (
          <img
            src={mediaUrl(tournament.clubLogoUrl)}
            alt={tournament.clubName}
            className="public-tournament__logo"
          />
        )}
        <div>
          <p className="public-tournament__club">{tournament.clubName}</p>
          <h1>{tournament.name}</h1>
          {tournament.description && (
            <p className="public-tournament__desc">{tournament.description}</p>
          )}
          {(tournament.startDate || tournament.endDate) && (
            <p className="public-tournament__dates">
              {formatDate(tournament.startDate)}
              {tournament.endDate && tournament.endDate !== tournament.startDate && (
                <> – {formatDate(tournament.endDate)}</>
              )}
            </p>
          )}
        </div>
        <div className="public-tournament__badges">
          {liveFixtures.length > 0 && (
            <span className="tournament-badge tournament-badge--live">Live</span>
          )}
          {liveFixtures.length === 0 && tournament.status === 'FINAL' && (
            <span className="tournament-badge tournament-badge--final">Final</span>
          )}
          {liveFixtures.length === 0 && tournament.status !== 'FINAL' && upcomingFixtures.length > 0 && (
            <span className="tournament-badge tournament-badge--scheduled">In progress</span>
          )}
        </div>
      </header>

      {liveFixtures.length > 0 && (
        <div className="public-tournament__live-banner">
          🔴 {liveFixtures.length} match{liveFixtures.length !== 1 ? 'es' : ''} in progress
        </div>
      )}

      <div className="public-tournament__actions">
        <button type="button" className="btn btn-primary" onClick={handleShare}>
          Share on WhatsApp
        </button>
      </div>

      {!hasFixtures ? (
        <section className="public-tournament__fixtures">
          <h2>Matches</h2>
          <p>No matches scheduled yet.</p>
        </section>
      ) : (
        <>
          {liveFixtures.length > 0 && (
            <section className="public-tournament__fixtures public-tournament__fixtures--live">
              <h2>Live now</h2>
              <div className="tournament-fixtures">
                {liveFixtures.map((fixture) => (
                  <FixtureCard key={fixture.id} fixture={fixture} />
                ))}
              </div>
            </section>
          )}

          {upcomingFixtures.length > 0 && (
            <section className="public-tournament__fixtures public-tournament__fixtures--upcoming">
              <h2>Upcoming matches</h2>
              <div className="tournament-fixtures">
                {upcomingFixtures.map((fixture) => (
                  <FixtureCard key={fixture.id} fixture={fixture} />
                ))}
              </div>
            </section>
          )}

          {completedFixtures.length > 0 && (
            <section className="public-tournament__fixtures public-tournament__fixtures--results">
              <h2>Results</h2>
              <div className="tournament-fixtures">
                {completedFixtures.map((fixture) => (
                  <FixtureCard key={fixture.id} fixture={fixture} />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {topScorers.length > 0 && (
        <section className="public-tournament__stats card">
          <h2>Top scorers</h2>
          <table className="tournament-stats-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Goals</th>
                <th>Assists</th>
              </tr>
            </thead>
            <tbody>
              {topScorers.map((p, i) => (
                <tr key={p.playerId}>
                  <td>{i + 1}</td>
                  <td>{p.playerName}</td>
                  <td>{p.goals}</td>
                  <td>{p.assists}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {(tournament.teamPhotoUrls || []).length > 0 && (
        <section className="public-tournament__photos card">
          <h2>Team photos</h2>
          <div className="public-tournament__photo-grid">
            {(tournament.teamPhotoUrls || []).map((url) => (
              <img key={url} src={mediaUrl(url)} alt="Team" loading="lazy" />
            ))}
          </div>
        </section>
      )}

      {(tournament.memberPhotos || []).length > 0 && (
        <section className="public-tournament__photos card">
          <h2>Players</h2>
          <div className="public-tournament__member-grid">
            {(tournament.memberPhotos || []).map((member) => (
              <div key={member.playerId} className="public-tournament__member-card">
                <img src={mediaUrl(member.photoUrl)} alt={member.playerName} loading="lazy" />
                <span>{member.playerName}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {(tournament.awards || []).some((a) => a.playerName || a.photoUrl) && (
        <section className="public-tournament__awards card">
          <h2>Awards</h2>
          <div className="public-tournament__awards-grid">
            {(tournament.awards || [])
              .filter((a) => a.playerName || a.photoUrl)
              .map((award) => {
                const labels = {
                  MVP: 'MVP',
                  BEST_GOALKEEPER: 'Best Goalkeeper',
                  BEST_FORWARD: 'Best Forward',
                  BEST_DEFENDER: 'Best Defender',
                };
                return (
                  <div key={award.type} className="public-tournament__award-card">
                    {award.photoUrl && (
                      <img src={mediaUrl(award.photoUrl)} alt={award.playerName || labels[award.type]} loading="lazy" />
                    )}
                    <div className="public-tournament__award-info">
                      <span className="public-tournament__award-label">{labels[award.type] || award.type}</span>
                      {award.playerName && <strong>{award.playerName}</strong>}
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      )}

      <footer className="public-tournament__footer">
        <p>Powered by Daily Sports Life</p>
      </footer>
    </div>
  );
};

export default PublicClubTournament;
